import { Controller } from "@hotwired/stimulus"
import { createNoise2D, createNoise3D, createNoise4D } from 'simplex-noise'

export default class extends Controller {
  static targets = ['canvas']
  static values = {
    primaryColor: String,
    secondaryColor: String,
    adaptToTheme: { type: Boolean, default: false }
  }

  connect() {
    if (!this.hasCanvasTarget) return

    const canvas = this.canvasTarget
    const ctx = canvas.getContext('2d')
    const canvasHeight = canvas.height
    const canvasWidth = canvas.width

    const lineWidthRelative = 0.04
    const noiseFactor = 15
    const noiseZoom = 0.001
    const numberOfPointsRelative = 0.5

    const lineWidth = lineWidthRelative * canvasHeight
    const numberOfPoints = numberOfPointsRelative * canvasHeight
    const deltaAngle = (Math.PI * 2) / numberOfPoints
    const radius = (canvasHeight - lineWidth) / 2
    const cx1 = canvasHeight / 2
    const cx2 = canvasWidth - (canvasHeight / 2)
    const cy = canvasHeight / 2

    const simplex = {
      noise2D: createNoise2D(),
      noise3D: createNoise3D(),
      noise4D: createNoise4D()
    }
    ctx.lineWidth = lineWidth

    this.color1 = this.primaryColorValue || '#00171F'
    this.color2 = this.secondaryColorValue || '#29A37A'
    this.resolveColors()

    const calcPoint = (angle, cx, timestamp) => {
      let x = cx + radius * Math.sin(angle)
      let y = cy + radius * Math.cos(angle)
      const n = (((simplex.noise3D((cx + x) * noiseZoom, (cy + y) * noiseZoom, timestamp / 3000)) - 1) / 2) * noiseFactor
      x = cx + (radius + n) * Math.sin(angle)
      y = cy + (radius + n) * Math.cos(angle)
      return [x, y]
    }

    const drawCircle = (side, timestamp) => {
      ctx.strokeStyle = side === 'left' ? this.color2 : this.color1
      ctx.beginPath()
      for (let angle = 0; angle < Math.PI * 2; angle += deltaAngle) {
        ctx.lineTo(...calcPoint(angle, side === 'left' ? cx1 : cx2, timestamp))
      }
      ctx.closePath()
      ctx.stroke()
    }

    const draw = (timestamp = 0) => {
      this.rafId = requestAnimationFrame(draw)
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
      drawCircle('left', timestamp)
      drawCircle('right', timestamp)
    }

    this.onThemeChanged = () => this.resolveColors()
    document.addEventListener('depozen:theme-changed', this.onThemeChanged)

    this.themeObserver = new MutationObserver(() => this.resolveColors())
    if (document.body) {
      this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['data-bs-theme'] })
    }
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })

    draw()
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    document.removeEventListener('depozen:theme-changed', this.onThemeChanged)
    this.themeObserver?.disconnect()
  }

  // Un rafraîchissement morphé (`turbo-refresh-method: morph`) garde le même élément : seul
  // l'attribut de couleur change, sans reconnexion. Sans ces callbacks, le menu natif
  // rechargé après un changement d'espace dessinait encore les anneaux de l'espace quitté.
  primaryColorValueChanged() {
    this.resolveColors()
  }

  secondaryColorValueChanged() {
    this.resolveColors()
  }

  adaptToThemeValueChanged() {
    this.resolveColors()
  }

  resolveColors() {
    const base1 = this.primaryColorValue || '#00171F'
    const base2 = this.secondaryColorValue || '#29A37A'

    if (!this.adaptToThemeValue) {
      this.color1 = base1
      this.color2 = base2
      return
    }

    const theme =
      document.body?.getAttribute('data-bs-theme') ||
      document.documentElement.getAttribute('data-bs-theme')

    if (theme === 'dark') {
      this.color1 = this.lightenForDark(base1)
      this.color2 = this.lightenForDark(base2)
    } else {
      this.color1 = base1
      this.color2 = base2
    }
  }

  lightenForDark(hex) {
    if (!hex || typeof hex !== 'string') return hex
    const normalized = hex.trim().toLowerCase()
    if (
      normalized === '#00171f' ||
      normalized === '#000' ||
      normalized === '#000000' ||
      normalized === 'black'
    ) {
      return '#eee'
    }
    const match = normalized.match(/^#([0-9a-f]{6})$/i)
    if (!match) return hex
    const n = parseInt(match[1], 16)
    const r = (n >> 16) & 255
    const g = (n >> 8) & 255
    const b = n & 255
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance < 0.35 ? '#eee' : hex
  }
}
